import { useMemo, useState, type ReactNode } from "react";
import type { ColumnDef, ExpandedState, SortingState } from "@tanstack/react-table";
import { Badge, Skeleton, cn } from "@fox1ck-org/ui";
import { LanderType, RiskStatus } from "@gen/tracker/v1/lander_pb";
import { OfferStatus, type PayoutConfig } from "@gen/tracker/v1/offer_pb";
import { PayoutModel } from "@gen/tracker/v1/affiliate_network_pb";

import { ListView } from "@/components/list-view";
import { money } from "@/components/offers/labels";
import { formatDayRange, formatInt } from "@/lib/format";
import { flatten, type ReportNode } from "@/lib/report";
import { useT, type TKey } from "@/lib/i18n";
import { dimCount, SPEND_WITHOUT_CLICKS } from "./names";
import { betterOf, cellText, type ReportColumn } from "./columns";
import { MetricDelta, MetricValue, WithDelta } from "./cells";
import type { ReportEngine } from "./engine";

const LANDER_TYPE: Partial<Record<LanderType, TKey>> = {
  [LanderType.LANDING]: "rep.lander.landing",
  [LanderType.PRELANDING]: "rep.lander.prelanding",
  [LanderType.VSL]: "rep.lander.vsl",
};
const PAYOUT_MODEL: Partial<Record<PayoutModel, string>> = {
  [PayoutModel.CPA]: "CPA",
  [PayoutModel.CPL]: "CPL",
  [PayoutModel.CPS]: "CPS",
  [PayoutModel.REVSHARE]: "RevShare",
};

/**
 * «22,00 $ · CPA»: an offer's payout as the partner states it (доска
 * ReportOffers). The amount is the server's decimal string («22.000000») and
 * goes through the app's one money spelling; no amount, no cell. Pure.
 */
export function payoutText(payout: PayoutConfig | undefined): string | undefined {
  const amount = payout?.amount;
  if (!amount?.amount?.trim()) return undefined;
  const model = payout?.model !== undefined ? PAYOUT_MODEL[payout.model] : undefined;
  return model ? `${money(amount.amount, amount.currency)} · ${model}` : money(amount.amount, amount.currency);
}

/**
 * The report's tree (доски Report, ReportCompare, ReportLanders,
 * ReportOffers): every level is the server's row, the bands are the
 * campaigns list's (Трафик · Конверсии · Деньги), «Итого» is the server's
 * totals. With «Сравнить» on, each value carries its change under it
 * («Только Δ») or the two periods stand side by side with Δ («Рядом»).
 */
export function ReportTable({ engine, className }: { engine: ReportEngine; className?: string }) {
  const t = useT();
  const { state, schema, columns, tree, totals, prevTotals, names, compare, dimLabel } = engine;
  const dims = state.dims;
  // Two short levels open whole (доски ReportOffers, ReportLanders); a deep
  // tree opens nothing until asked (доска Report). The person's toggles win.
  const [own, setOwn] = useState<{ key: string; state: ExpandedState } | undefined>();
  const treeKey = `${dims.join(",")}|${state.params.from}|${state.params.to}`;
  const all = flatten(tree).length;
  const expanded: ExpandedState = own?.key === treeKey ? own.state : dims.length === 2 && all <= 60 ? true : {};
  const setExpanded = (u: ExpandedState | ((old: ExpandedState) => ExpandedState)) => setOwn({ key: treeKey, state: typeof u === "function" ? u(expanded) : u });
  const side = !!compare && state.compareView === "side";
  const withDelta = !!compare && !side;

  const profitMax = useMemo(() => flatten(tree).reduce((m, n) => Math.max(m, Math.abs(n.v.profit ?? 0)), Math.abs(totals?.profit ?? 0)), [tree, totals]);
  const curLabel = formatDayRange(state.params.from, state.params.to);
  const prevLabel = compare ? formatDayRange(compare.from, compare.to) : "";

  const headerOf = (c: ReportColumn) => (c.custom ? c.label : c.short ? t(c.short) : c.label.includes(".") && !c.label.includes(" ") ? t(c.label as TKey) : c.label);
  const titleOf = (c: ReportColumn) => [headerOf(c), (c.custom ?? schema?.metricsByKey.get(c.key))?.description].filter(Boolean).join(": ");

  const tableColumns = useMemo<ColumnDef<ReportNode, unknown>[]>(() => {
    const metricCol = (c: ReportColumn): ColumnDef<ReportNode, unknown> => {
      const better = betterOf(c, schema);
      return {
        id: c.key,
        accessorFn: (r) => r.v[c.key] ?? null,
        enableSorting: c.format !== "outcome",
        size: c.format === "outcome" ? 104 : c.format === "profit" ? 110 : 56,
        meta: { align: "right" },
        header: () => (
          <span className="whitespace-nowrap" title={titleOf(c)}>
            {headerOf(c)}
          </span>
        ),
        cell: ({ row }) => {
          const n = row.original;
          const value = <MetricValue c={c} v={n.v} max={profitMax} strong={n.depth === 0} />;
          if (!withDelta || c.format === "outcome") return value;
          return (
            <WithDelta delta={<MetricDelta c={c} better={better} cur={n.v[c.key]} prev={n.prev?.[c.key]} />} title={t("rep.prevValue", { value: cellText(c, n.prev?.[c.key]) })}>
              {value}
            </WithDelta>
          );
        },
      };
    };
    // «Рядом»: a metric is three columns — this period, the other, Δ.
    const sideCols = (c: ReportColumn): ColumnDef<ReportNode, unknown> => {
      const better = betterOf(c, schema);
      const num = (pick: (n: ReportNode) => ReactNode, id: string, label: string, sortable: boolean): ColumnDef<ReportNode, unknown> => ({
        id,
        accessorFn: (r) => r.v[c.key] ?? null,
        enableSorting: sortable,
        size: 72,
        meta: { align: "right" },
        header: () => <span className="whitespace-nowrap normal-case">{label}</span>,
        cell: ({ row }) => pick(row.original),
      });
      return {
        id: `cmp-${c.key}`,
        header: headerOf(c),
        columns: [
          num((n) => <span className={cn("font-mono text-sm tabular-nums", n.depth === 0 && "font-semibold")}>{cellText(c, n.v[c.key])}</span>, c.key, curLabel, true),
          num((n) => <span className="font-mono text-sm tabular-nums text-muted-foreground">{cellText(c, n.prev?.[c.key])}</span>, `${c.key}__prev`, prevLabel, false),
          num((n) => <MetricDelta c={c} better={better} cur={n.v[c.key]} prev={n.prev?.[c.key]} className="text-sm" />, `${c.key}__delta`, "Δ", false),
        ],
      };
    };
    const band = (id: "traffic" | "conversions" | "money") => {
      const leaves = columns.filter((c) => c.band === id).map(metricCol);
      return leaves.length ? [{ id: `band-${id}`, header: t(`rep.band.${id}` as TKey), columns: leaves }] : [];
    };
    const payout: ColumnDef<ReportNode, unknown>[] = dims.includes("offer")
      ? [
          {
            id: "payout",
            header: t("rep.col.payout"),
            enableSorting: false,
            size: 110,
            meta: { align: "right" },
            cell: ({ row }) => {
              const n = row.original;
              if (dims[n.depth] !== "offer") return null;
              const o = names.offers?.get(n.path[n.depth]!);
              const text = payoutText(o?.payout);
              return text ? <span className="whitespace-nowrap font-mono text-xs text-muted-foreground">{text}</span> : null;
            },
          },
        ]
      : [];
    const kind: ColumnDef<ReportNode, unknown>[] = dims[0] === "landing"
      ? [
          {
            id: "lander_kind",
            header: t("rep.lander.type"),
            enableSorting: false,
            size: 150,
            cell: ({ row }) => <LanderKind engine={engine} n={row.original} />,
          },
        ]
      : [];
    return [
      {
        id: "name",
        header: dims.map(dimLabel).join(" › "),
        enableSorting: false,
        size: 290,
        cell: ({ row }) => <NameCell engine={engine} n={row.original} />,
      },
      ...kind,
      ...(side ? sideColumns(columns).map(sideCols) : [...band("traffic"), ...band("conversions"), ...payout, ...band("money")]),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, dims, names.name, names.offers, names.landers, profitMax, schema, t, withDelta, side, curLabel, prevLabel]);

  const totalsRow = useMemo(() => {
    if (!totals) return undefined;
    // «Расход без кликов» is a line of the report, not one of its groups.
    const top = (engine.main.data?.total ?? tree.length) - (tree.some((n) => n.path[0] === SPEND_WITHOUT_CLICKS) ? 1 : 0);
    const out: Record<string, ReactNode> = {
      name: <span className="font-semibold">{t("rep.total", { what: dimCount(t, dims[0]!, top, dimLabel(dims[0]!), formatInt(top)) })}</span>,
    };
    for (const c of side ? sideColumns(columns) : columns) {
      const better = betterOf(c, schema);
      if (side) {
        out[c.key] = <span className="font-mono text-sm font-semibold tabular-nums">{cellText(c, totals[c.key])}</span>;
        out[`${c.key}__prev`] = <span className="font-mono text-sm font-semibold tabular-nums text-muted-foreground">{cellText(c, prevTotals?.[c.key])}</span>;
        out[`${c.key}__delta`] = <MetricDelta c={c} better={better} cur={totals[c.key]} prev={prevTotals?.[c.key]} className="text-sm font-semibold" />;
        continue;
      }
      const value = <MetricValue c={c} v={totals} max={profitMax} strong />;
      out[c.key] =
        withDelta && c.format !== "outcome" ? (
          <WithDelta delta={<MetricDelta c={c} better={better} cur={totals[c.key]} prev={prevTotals?.[c.key]} />}>{value}</WithDelta>
        ) : (
          value
        );
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals, prevTotals, columns, profitMax, dims, t, engine.main.data?.total, tree.length, withDelta, side, schema]);

  const sorting: SortingState = [{ id: state.sort.key, desc: state.sort.desc }];

  return (
    <ListView
      // Fourteen columns on a 1512 screen (доска Report): the cells keep 8px a side, not the list's 12.
      className={cn("[&_td]:px-2 [&_th]:px-2", className)}
      failure={engine.main.error ? { title: t("rep.error"), error: engine.main.error, retry: () => void engine.main.refetch() } : undefined}
      columns={tableColumns}
      data={tree}
      getRowId={(n) => n.id}
      getSubRows={(n) => (n.children.length ? n.children : undefined)}
      expanded={expanded}
      onExpandedChange={setExpanded}
      expandColumnId="name"
      loading={engine.main.isLoading || (!engine.main.data && !engine.noCampaigns)}
      density={withDelta ? "default" : "compact"}
      bandDividers
      sorting={sorting}
      onSortingChange={(u) => {
        const next = typeof u === "function" ? u(sorting) : u;
        const s = next[0];
        state.setSort(s ? { key: s.id, desc: s.desc } : null);
      }}
      manualSorting
      rowClassName={(n) => (n.depth === 0 && n.children.length > 0 ? "bg-surface-2/60" : undefined)}
      totals={totalsRow}
    />
  );
}

/**
 * «Рядом» is three columns a metric, so four metrics fill a 1512 screen
 * (доска ReportCompare: Клики · Лиды · Профит · ROI). The reader's columns in
 * this order of preference, then whatever else they picked. Pure.
 */
export function sideColumns(columns: ReportColumn[]): ReportColumn[] {
  const first = ["clicks", "leads", "profit", "roi", "cr", "revenue", "cost"];
  const pool = columns.filter((c) => c.format !== "outcome");
  return [...first.flatMap((k) => pool.filter((c) => c.key === k)), ...pool.filter((c) => !first.includes(c.key))].slice(0, 4);
}

/** A row's name: the value as a person reads it, how many under it, and what the catalog knows of it. */
function NameCell({ engine, n }: { engine: ReportEngine; n: ReportNode }) {
  const t = useT();
  const dims = engine.state.dims;
  const key = dims[n.depth]!;
  const value = n.path[n.depth]!;
  const lander = key === "landing" ? engine.names.landers?.get(value) : undefined;
  const offer = key === "offer" ? engine.names.offers?.get(value) : undefined;
  return (
    <span className="flex min-w-0 max-w-72.5 items-center gap-2">
      {lander?.currentVersion ? (
        <Badge variant="neutral" className="shrink-0 font-mono text-xs">
          v{lander.currentVersion}
        </Badge>
      ) : null}
      {engine.names.pending(key, value) ? (
        <Skeleton className="h-3.5 w-48 shrink" aria-label={t("rep.naming")} />
      ) : (
        <span className={cn("truncate", n.depth === 0 && "font-medium", key === "landing" && "font-mono text-sm")} title={engine.names.name(key, value)}>
          {engine.names.name(key, value)}
        </span>
      )}
      {n.children.length > 0 ? (
        <span className="shrink-0 text-xs text-muted-foreground">{dimCount(t, dims[n.depth + 1]!, n.children.length, engine.dimLabel(dims[n.depth + 1]!), formatInt(n.children.length))}</span>
      ) : null}
      {offer?.status === OfferStatus.PAUSED ? (
        <Badge variant="neutral" className="shrink-0">
          {t("rep.offer.paused")}
        </Badge>
      ) : offer?.status === OfferStatus.CAPPED ? (
        <Badge variant="warn" className="shrink-0">
          {t("rep.offer.capped")}
        </Badge>
      ) : null}
    </span>
  );
}

/** «Преленд · Опасно»: what the catalog says of a lander row (доска ReportLanders). */
function LanderKind({ engine, n }: { engine: ReportEngine; n: ReportNode }) {
  const t = useT();
  if (engine.state.dims[n.depth] !== "landing") return null;
  const lander = engine.names.landers?.get(n.path[n.depth]!);
  const type = lander ? LANDER_TYPE[lander.landerType] : undefined;
  return (
    <span className="flex items-center gap-1.5">
      {type ? <Badge variant="outline">{t(type)}</Badge> : null}
      {lander?.riskStatus === RiskStatus.DANGER ? <Badge variant="danger">{t("rep.lander.danger")}</Badge> : null}
    </span>
  );
}
